/**
 * BlockRenderCoordinator - Coordinates block rendering for canvas nodes
 *
 * Responsibilities:
 * - Connect BlockRegistry with BlockPreviewRenderer
 * - Provide subscription mechanism for reactive updates
 * - Delegate rendering to BlockPreviewRenderer
 *
 * Architecture:
 * - Registry: stores StrategyBlock data (single source of truth)
 * - Coordinator: bridges Registry ↔ Renderer
 * - Renderer: visual rendering logic
 *
 * Usage:
 * ```tsx
 * const coordinator = blockRenderCoordinator;
 *
 * // Render block by ID
 * const preview = coordinator.renderById(blockId, { scale: 0.5 });
 *
 * // Subscribe to changes
 * const unsubscribe = coordinator.subscribe(blockId, () => {
 *   // Block changed, re-render
 * });
 * ```
 *
 * Sprint: sprint-8
 * Task: Entity Node Unification
 * Pattern: Mirrors ScreenRenderCoordinator
 */

import React from 'react';
import type { StrategyBlock } from '@segmently/schema-models/strategy';
import { blockPreviewRenderer, type RenderOptions } from './BlockPreviewRenderer';
import { blockRegistry, BlockRegistry } from './BlockRegistry';

/**
 * Singleton coordinator for block rendering
 */
export class BlockRenderCoordinator {
  private static instance: BlockRenderCoordinator;
  private registry: BlockRegistry;

  private constructor(registry: BlockRegistry) {
    this.registry = registry;
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): BlockRenderCoordinator {
    if (!BlockRenderCoordinator.instance) {
      BlockRenderCoordinator.instance = new BlockRenderCoordinator(blockRegistry);
    }
    return BlockRenderCoordinator.instance;
  }

  /**
   * Subscribe to block changes
   *
   * @param blockId - Block ID to watch
   * @param callback - Called when block changes
   * @returns Unsubscribe function
   */
  public subscribe(blockId: string, callback: () => void): () => void {
    return this.registry.subscribe(blockId, callback);
  }

  /**
   * Get StrategyBlock by entityId (from registry)
   */
  public getBlock(blockId: string): StrategyBlock | undefined {
    return this.registry.get(blockId);
  }

  /**
   * Check if block exists (in registry)
   */
  public hasBlock(blockId: string): boolean {
    return this.registry.has(blockId);
  }

  /**
   * Render block by entityId
   *
   * @param blockId - The block entityId to render
   * @param options - Rendering options (scale, interactive, etc.)
   * @returns Rendered React component or fallback
   */
  public renderById(
    blockId: string,
    options?: Partial<RenderOptions>
  ): React.ReactNode {
    const block = this.registry.get(blockId);

    if (!block) {
      console.warn(`BlockRenderCoordinator: Block not found in registry: ${blockId}`);
      return this.renderFallback(blockId, 'Block not in registry');
    }

    try {
      return blockPreviewRenderer.renderBlock(block, options);
    } catch (error) {
      console.error(`BlockRenderCoordinator: Failed to render block ${blockId}`, error);
      return this.renderFallback(blockId, 'Render error');
    }
  }

  /**
   * Render fallback UI when block cannot be rendered
   */
  private renderFallback(blockId: string, reason: string): React.ReactNode {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          color: '#999',
          fontSize: '12px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>⚠️</div>
        <div style={{ fontWeight: 600 }}>{reason}</div>
        <div style={{ fontSize: '10px', marginTop: '4px' }}>ID: {blockId}</div>
      </div>
    );
  }

  /**
   * Get registry stats
   */
  public getStats(): { totalBlocks: number; blockIds: string[] } {
    return {
      totalBlocks: this.registry.getAll().length,
      blockIds: this.registry.getAllIds(),
    };
  }

  /**
   * Debug: Log coordinator state
   */
  public debug(): void {
    console.log('[BlockRenderCoordinator] Debug:');
    this.registry.debug();
  }
}

/**
 * Export singleton instance
 */
export const blockRenderCoordinator = BlockRenderCoordinator.getInstance();
